import type { ContractFunctionConfig } from '@eulerxyz/evc-sdk'
import { Address, stringToHex } from 'viem'
import { BaseTransactionFactory } from './BaseTransactionFactory'
import {
  type GenericSwapTransactionItem,
  type SwapDepositTransactionItem,
  type SwapMulticallTransactionItem,
  type SwapRepayAndDepositTransactionItem,
  type SwapSweepTransactionItem,
  type SwapTransactionItem,
  type UniswapV2SwapTransactionItem,
  type UniswapV3SwapTransactionItem,
  isGenericSwapTransactionItem,
  isSwapDepositTransactionItem,
  isSwapMulticallTransactionItem,
  isSwapRepayAndDepositTransactionItem,
  isSwapSweepTransactionItem,
  isUniswapV2SwapTransactionItem,
  isUniswapV3SwapTransactionItem,
} from './types'

export class SwapTransactionFactory extends BaseTransactionFactory {
  HANDLER_GENERIC = 'Generic'
  HANDLER_GENERIC_32BYTES = stringToHex(this.HANDLER_GENERIC, {
    size: 32,
  })
  HANDLER_UNISWAP_V2 = 'UniswapV2'
  HANDLER_UNISWAP_V2_32BYTES = stringToHex(this.HANDLER_UNISWAP_V2, {
    size: 32,
  })
  HANDLER_UNISWAP_V3 = 'UniswapV3'
  HANDLER_UNISWAP_V3_32BYTES = stringToHex(this.HANDLER_UNISWAP_V3, {
    size: 32,
  })

  typeGuardConvertMultiple(_swapTransactions: SwapTransactionItem[]): ContractFunctionConfig[] {
    const contractCallReadyTxs: ContractFunctionConfig[] = []
    for (const tx of _swapTransactions) {
      const call = this.typeGuardConvertSingle(tx)
      contractCallReadyTxs.push(call)
    }
    return contractCallReadyTxs
  }

  typeGuardConvertSingle(transaction: SwapTransactionItem): ContractFunctionConfig {
    if (isSwapMulticallTransactionItem(transaction)) {
      return this.convertSwapMulticallTransaction(transaction)
    }

    if (isUniswapV2SwapTransactionItem(transaction)) {
      return this.convertUniswapV2SwapTransaction(transaction)
    }
    if (isUniswapV3SwapTransactionItem(transaction)) {
      return this.convertUniswapV3SwapTransaction(transaction)
    }
    if (isGenericSwapTransactionItem(transaction)) {
      return this.convertGenericSwapTransaction(transaction)
    }
    if (isSwapRepayAndDepositTransactionItem(transaction)) {
      return this.convertSwapRepayAndDepositTransaction(transaction)
    }
    if (isSwapDepositTransactionItem(transaction)) {
      return this.convertSwapDepositTransaction(transaction)
    }
    if (isSwapSweepTransactionItem(transaction)) {
      return this.convertSwapSweepTransaction(transaction)
    }

    throw this.errorUnsupportedTransactionType(transaction)
  }

  convertGenericSwapTransaction(
    transaction: GenericSwapTransactionItem,
  ): ContractFunctionConfig<typeof this.contractBook.swapper.abi, 'swap'> {
    const swapperAddress = this.#getSwapperAddress(transaction)
    return {
      functionName: 'swap',
      abi: this.contractBook.swapper.abi,
      address: swapperAddress,
      onBehalfOfAccount: transaction.from,
      value: 0n,
      args: [
        {
          handler: this.HANDLER_GENERIC_32BYTES,
          mode: BigInt(transaction.mode),
          account: transaction.from,
          tokenIn: transaction.asset.address,
          tokenOut: transaction.outputAsset.address,
          vaultIn: transaction.vaultIn,
          accountIn: transaction.accountIn,
          receiver: transaction.to,
          amountOut: BigInt(transaction.outputValue),
          data: transaction.data,
        },
      ],
    }
  }

  convertUniswapV2SwapTransaction(
    transaction: UniswapV2SwapTransactionItem,
  ): ContractFunctionConfig<typeof this.contractBook.swapper.abi, 'swap'> {
    const swapperAddress = this.#getSwapperAddress(transaction)
    return {
      functionName: 'swap',
      abi: this.contractBook.swapper.abi,
      address: swapperAddress,
      onBehalfOfAccount: transaction.from,
      value: 0n,
      args: [
        {
          handler: this.HANDLER_UNISWAP_V2_32BYTES,
          mode: BigInt(transaction.mode),
          account: transaction.from,
          tokenIn: transaction.asset.address,
          tokenOut: transaction.outputAsset.address,
          vaultIn: transaction.vaultIn,
          accountIn: transaction.accountIn,
          receiver: transaction.to,
          amountOut: BigInt(transaction.outputValue),
          data: transaction.data,
        },
      ],
    }
  }

  convertUniswapV3SwapTransaction(
    transaction: UniswapV3SwapTransactionItem,
  ): ContractFunctionConfig<typeof this.contractBook.swapper.abi, 'swap'> {
    const swapperAddress = this.#getSwapperAddress(transaction)
    return {
      functionName: 'swap',
      abi: this.contractBook.swapper.abi,
      address: swapperAddress,
      onBehalfOfAccount: transaction.from,
      value: 0n,
      args: [
        {
          handler: this.HANDLER_UNISWAP_V3_32BYTES,
          mode: BigInt(transaction.mode),
          account: transaction.from,
          tokenIn: transaction.asset.address,
          tokenOut: transaction.outputAsset.address,
          vaultIn: transaction.vaultIn,
          accountIn: transaction.accountIn,
          receiver: transaction.to,
          amountOut: BigInt(transaction.outputValue),
          data: transaction.data,
        },
      ],
    }
  }

  convertSwapMulticallTransaction(
    transaction: SwapMulticallTransactionItem,
  ): ContractFunctionConfig<typeof this.contractBook.swapper.abi, 'multicall'> {
    return {
      abi: this.contractBook.swapper.abi,
      functionName: 'multicall',
      address: transaction.swapperAddress,
      onBehalfOfAccount: transaction.from,
      value: 0n,
      args: [transaction.calls],
    }
  }

  convertSwapRepayAndDepositTransaction(
    transaction: SwapRepayAndDepositTransactionItem,
  ): ContractFunctionConfig<typeof this.contractBook.swapper.abi, 'repayAndDeposit'> {
    const swapperAddress = this.#getSwapperAddress(transaction)
    return {
      functionName: 'repayAndDeposit',
      abi: this.contractBook.swapper.abi,
      address: swapperAddress,
      onBehalfOfAccount: transaction.from,
      value: 0n,
      args: [
        transaction.asset.address, // address token
        transaction.vaultIn, // address vault
        BigInt(transaction.repayAmount), // uint repayAmount
        transaction.to, // address account
      ],
    }
  }

  convertSwapDepositTransaction(
    transaction: SwapDepositTransactionItem,
  ): ContractFunctionConfig<typeof this.contractBook.swapper.abi, 'deposit'> {
    const swapperAddress = this.#getSwapperAddress(transaction)
    return {
      functionName: 'deposit',
      abi: this.contractBook.swapper.abi,
      address: swapperAddress,
      onBehalfOfAccount: transaction.from,
      value: 0n,
      args: [
        transaction.asset.address, // address token
        transaction.vaultIn, // address vault
        1n, // uint minAmount
        transaction.to, // address account
      ],
    }
  }

  convertSwapSweepTransaction(
    transaction: SwapSweepTransactionItem,
  ): ContractFunctionConfig<typeof this.contractBook.swapper.abi, 'sweep'> {
    const swapperAddress = this.#getSwapperAddress(transaction)
    return {
      functionName: 'sweep',
      abi: this.contractBook.swapper.abi,
      address: swapperAddress,
      onBehalfOfAccount: transaction.from,
      value: 0n,
      args: [
        transaction.asset.address, // address token
        1n, // uint minAmount, skip sweep if 0 balance
        transaction.to, // address account
      ],
    }
  }

  #getSwapperAddress(transaction: SwapTransactionItem): Address {
    const swapperAddress = this.contractBook.swapper.address[this.chainId]
    if (!swapperAddress) {
      // @ts-ignore - Error constructor signature mismatch
      throw new Error('Swapper not found for that chainId', {
        cause: { transaction, chainId: this.chainId },
      })
    }

    return swapperAddress
  }
}
